import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ChevronRight, Sparkles, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PanorixLogo, PanorixLogoCompact } from '@/components/PanorixLogo';
import PerceptionField from '@/components/PerceptionField';
import { fadeUp, staggerChildren } from '@/lib/motion';

const TIERS = [
  { n: '01', name: 'Portfolio & Program', count: 3, modules: ['Portfolio Dashboard', 'Program Management', 'RAID Management'] },
  { n: '02', name: 'Schedule & Progress',  count: 3, modules: ['Macro Schedule (CPM)', '4D Timeliner + 3D Viewer', 'Drone & LiDAR Progress'] },
  { n: '03', name: 'Construction Execution', count: 3, modules: ['Workfront — AWP/IWP', 'Piping Workfront', 'Structural Workfront'] },
  { n: '04', name: 'Cost & Commercial',    count: 3, modules: ['Earned Value Mgmt', 'CBS Matrix · Budgets', 'Contracts & Claims (CLM)'] },
  { n: '05', name: 'Material & Delivery',  count: 3, modules: ['Procurement (MCI)', 'Engineering (DCI)', 'Manpower & Equipment'] },
  { n: '06', name: 'Command & Cognition',  count: 3, modules: ['Sūtradhār AI', 'Scan-to-Model', 'Reports & Insights'] },
];

const CAPABILITIES = [
  { n: '01', t: 'Portfolio-level health score',           d: 'Aggregated Cost / Schedule / Quality / HSE metrics across every active project, with board-ready exports.' },
  { n: '02', t: 'Industry-standard CPM / Gantt parity',   d: 'Full forward/backward pass, critical path, resource loading, and bi-directional XER / MPP round-trip.' },
  { n: '03', t: '4D Timeliner on native 3D',               d: 'Watch construction sequence on the 3D model. Schedule-vs-3D linkage with clash detection over time.' },
  { n: '04', t: 'Real-time EVM (CPI / SPI)',               d: 'Continuous CPI/SPI with configurable thresholds, period-over-period trending, EAC/ETC forecasting.' },
  { n: '05', t: 'Sūtradhār AI integration',                d: 'Natural-language queries across 18 modules. Anomaly detection, delay analysis, contract intelligence.' },
  { n: '06', t: 'Role-based command centre',               d: 'Custom widgets for Owner, EPC, PMC, Site views. Alert engine with escalation rules and SLA tracking.' },
  { n: '07', t: 'Workfront readiness scoring',             d: 'AWP-based IWP scoring against 8 constraints. Rolling 3-week lookahead. 15–25% crew productivity uplift.' },
  { n: '08', t: 'Drone & LiDAR deviation analysis',        d: 'Weekly captures auto-aligned to 3D. As-built vs as-planned heatmaps, volumetric progress measurement.' },
];

const OUTCOMES = [
  { v: '$33 M', h: 'average savings on a $600 M EPC project',         b: 'Workfront + Material integration + EVM discipline + claim prevention.' },
  { v: '64%',     h: 'reduction in management review time on reconciliation', b: 'From 42% of review time on reconciliation to <15%.' },
  { v: '3.2× faster', h: 'decision-making in executive steering committees',  b: 'Measured across 11 deployments in India / GCC.' },
];

const INDUSTRIES = [
  'Oil, Gas & Energy — Upstream · Pipeline · LNG · Downstream · Hydrogen',
  'Power & Utilities — Generation · T&D · BESS · Desalination',
  'Chemicals — Basic · Specialty',
  'Infrastructure — Roads · Metros · Data Centers · Bridges',
  'Metals, Mining & Minerals',
  'Manufacturing — Discrete · Life Sciences · Pulp & Paper',
  'Marine — Ports · Shipbuilding',
];

export default function LandingPage() {
  const [showAuth, setShowAuth] = useState(false);
  const [showTrial, setShowTrial] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Trial form state
  const [trialFirst, setTrialFirst] = useState('');
  const [trialLast, setTrialLast] = useState('');
  const [trialEmail, setTrialEmail] = useState('');
  const [trialCompany, setTrialCompany] = useState('');
  const [trialPassword, setTrialPassword] = useState('');
  const [trialSubmitting, setTrialSubmitting] = useState(false);
  const { login, user, logout, fetchPermissions, setUserData } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try { await login(loginEmail, loginPassword); navigate('/project-selector'); }
    catch (err) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.message
        || (err?.response?.status === 401 ? 'Invalid email or password.' : '')
        || (err?.response?.status === 503 ? 'Server is starting up. Please retry in a few seconds.' : '')
        || err?.message
        || 'Login failed. Please try again.';
      toast.error(detail);
    }
    finally { setSubmitting(false); }
  };

  const handleTrialRegister = async (e) => {
    e.preventDefault();
    if (trialPassword.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setTrialSubmitting(true);
    try {
      const r = await api.post('/auth/trial-register', {
        firstName: trialFirst, lastName: trialLast,
        email: trialEmail, company: trialCompany, password: trialPassword,
      });
      const data = r.data || {};
      if (data.token || data.accessToken) localStorage.setItem('ciq_token', data.token || data.accessToken);
      if (data.refreshToken) localStorage.setItem('ciq_refresh_token', data.refreshToken);
      if (data.user) localStorage.setItem('ciq_user', JSON.stringify(data.user));
      if (setUserData && data.user) setUserData(data.user);
      try { await fetchPermissions?.(); } catch {}
      toast.success('Welcome! Your 24-hour trial has started.');
      navigate('/project-selector?trial=1', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start trial.');
    } finally { setTrialSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 force-dark overflow-x-hidden" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Ambient grid + orbs */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.06]" style={{
        backgroundImage: 'linear-gradient(rgba(140,174,219,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(140,174,219,0.5) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }} />
      <div className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(30,78,140,0.32), transparent 70%)' }} />
      <div className="fixed -bottom-40 -right-40 w-[700px] h-[700px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(242,107,31,0.14), transparent 70%)' }} />

      {/* Header */}
      <header className="relative z-20 border-b border-zinc-800/70 backdrop-blur-md bg-[#0A0A0A]/80">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <PanorixLogoCompact size={180} forceTheme="dark" />
          <nav className="hidden md:flex items-center gap-8 text-[12px] tracking-[0.1em] uppercase text-zinc-500">
            <a href="#architecture" className="hover:text-zinc-100 transition">Architecture</a>
            <a href="#capabilities" className="hover:text-zinc-100 transition">Capabilities</a>
            <a href="#outcomes" className="hover:text-zinc-100 transition">Outcomes</a>
            <a href="#industries" className="hover:text-zinc-100 transition">Industries</a>
          </nav>
          <div className="flex items-center gap-3">
            {/* Start Free Trial — opens email/password trial registration */}
            <Button variant="ember" size="sm" onClick={() => setShowTrial(true)} data-testid="header-trial-btn"
              className="hidden md:inline-flex tracking-[0.18em] uppercase text-[11px]">
              <Sparkles className="w-3.5 h-3.5"/>
              Start Free Trial
            </Button>
            {user ? (
              <>
                <Button variant="default" size="sm" onClick={() => navigate('/project-selector')} data-testid="header-dashboard-btn"
                  className="tracking-[0.18em] uppercase text-[11px]">Go to Control Tower <ArrowRight className="w-3.5 h-3.5 ml-1"/></Button>
                <Button variant="outline" size="sm" onClick={async () => { try { await logout(); } catch {} window.location.reload(); }} data-testid="header-signout-btn"
                  className="tracking-[0.18em] uppercase text-[11px] border-zinc-700 text-zinc-200 hover:bg-zinc-900">Sign Out</Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => { setShowAuth(true); }} data-testid="header-login-btn"
                  className="tracking-[0.18em] uppercase text-[11px] border-zinc-700 text-zinc-200 hover:bg-zinc-900">Sign In</Button>
                <Button variant="default" size="sm" onClick={() => { setShowAuth(true); }} data-testid="header-cta-btn"
                  className="hidden sm:inline-flex tracking-[0.18em] uppercase text-[11px]">Access Control Tower <ArrowRight className="w-3.5 h-3.5 ml-1"/></Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-8 pt-16 pb-24">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 items-start">
          {/* Hero copy */}
          <div>
            <div className="text-eyebrow mb-3 text-zinc-500">PLATFORMS · ENTERPRISE DIGITAL · PANORIX</div>
            <div className="text-eyebrow-ember mb-8">CAPITAL PROJECTS CONTROL TOWER & COMMAND CENTRE</div>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <PanorixLogo width={640} forceTheme="dark" className="mb-8 max-w-full" />
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-4xl lg:text-[3.4rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-6 max-w-2xl">
              The single pane of glass for{' '}
              <em className="text-ember-500 font-medium">every dollar, every hour, every risk, every ton, every weld.</em>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base text-zinc-400 leading-relaxed mb-10 max-w-xl">
              Eighteen modules. One command canvas. From $12 M refinery revamps to $6 B metro corridors —
              Panorix gives owners, EPCs, and PMCs the same integrated visibility that NASA gives its mission controllers.
              Portfolio to weld. Board to bolt.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap items-center gap-3 mb-12">
              <Button variant="ember" size="xl" onClick={() => setShowTrial(true)} data-testid="hero-trial-cta"
                className="tracking-[0.18em] uppercase text-[12px]">
                <Sparkles className="w-3.5 h-3.5"/> Start 1-day Free Trial
              </Button>
              <a href="#architecture" data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-900/50 text-zinc-200 px-6 py-3.5 text-[12px] tracking-[0.18em] uppercase rounded-md transition">
                See the 18 modules
              </a>
            </motion.div>
          </div>

          {/* Hero stage — perception field */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="relative aspect-[680/580] rounded-2xl overflow-hidden"
            style={{ background: 'radial-gradient(ellipse at center, rgba(30,78,140,0.15), transparent 70%)', border: '1px solid rgba(30,78,140,0.3)' }}>
            <div className="absolute top-4 left-4 flex items-center gap-2 z-10 text-[10px] tracking-[0.2em] text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse"/> LIVE · 18 MODULES · 6 DIMENSIONS
            </div>
            <div className="absolute top-4 right-4 z-10 text-[10px] tracking-[0.2em] text-slate-500">
              PROJECT · $510 M · FEL-4
            </div>
            <PerceptionField />
          </motion.div>
        </div>
      </section>

      {/* IMPERATIVE — the seven-tools problem */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-8 py-24 border-t border-zinc-800/70">
        <div className="text-eyebrow-ember mb-4">THE CONTROL-TOWER IMPERATIVE</div>
        <h2 className="font-display text-3xl lg:text-[3rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-16 max-w-4xl">
          On a $600 M project, seven tools hold the truth.{' '}
          <em className="text-zinc-500 font-normal">Nobody holds the truth about all seven.</em>
        </h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { v: '7+', t: 'disconnected tools on a typical $600 M project', s: 'schedule, cost, 3D, drone, weld, material, RAID, HSE' },
            { v: '$4.5 M', t: 'cost of tool sprawl on a typical EPC',         s: 'licences + integration + reconciliation effort' },
            { v: '42%',  t: 'of management review time spent reconciling data', s: 'not making decisions' },
            { v: '11 days', t: 'average time to reconcile EVM across tools',  s: 'before steering committee meets' },
          ].map((c, i) => (
            <div key={i} className="border border-zinc-800/70 rounded-xl p-6 bg-zinc-900/40 backdrop-blur-sm hover:border-zinc-700 hover:bg-zinc-900/60 transition">
              <div className="font-mono text-3xl text-ember-500 mb-3 tabular-nums">{c.v}</div>
              <div className="text-sm text-zinc-200 mb-2 leading-snug">{c.t}</div>
              <div className="text-xs text-zinc-500 leading-relaxed">{c.s}</div>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center text-zinc-400 text-base">
          Seven tools. Seven dashboards. <em className="font-display text-ember-500 not-italic">Seven versions of truth.</em>
        </div>
      </section>

      {/* ARCHITECTURE — six tiers */}
      <section id="architecture" className="relative z-10 max-w-[1400px] mx-auto px-8 py-24 border-t border-zinc-800/70">
        <div className="text-eyebrow mb-4">PLATFORM ARCHITECTURE</div>
        <h2 className="font-display text-3xl lg:text-[3rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-4 max-w-4xl">
          Eighteen modules. Six tiers. <em className="text-ember-500 font-normal">One command canvas.</em>
        </h2>
        <p className="text-zinc-400 max-w-2xl mb-12">
          Hover any tier to expand its modules. Panorix's eighteen modules organise from portfolio governance through
          schedule, execution, cost, material, to command & cognition.
        </p>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}
          variants={staggerChildren(0.07)}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TIERS.map((tier) => (
            <motion.div key={tier.n} variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}
              className="group border border-zinc-800/70 rounded-xl p-6 bg-zinc-900/40 hover:bg-zinc-900/70 hover:border-steel-500/60 transition cursor-pointer">
              <div className="flex items-start justify-between mb-4">
                <div className="text-eyebrow">TIER · {tier.n}</div>
                <div className="font-mono text-xs text-ember-500 tracking-wider">{tier.count} MODULES</div>
              </div>
              <h3 className="text-xl font-medium text-zinc-50 mb-4">{tier.name}</h3>
              <ul className="space-y-2">
                {tier.modules.map((m) => (
                  <li key={m} className="flex items-center gap-2 text-sm text-zinc-400 group-hover:text-zinc-200 transition">
                    <ChevronRight className="w-3 h-3 text-steel-400 shrink-0"/> {m}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
        <div className="mt-12 text-zinc-500 text-sm italic font-display max-w-3xl">
          Eighteen modules. Each stands alone. Together, they form one cognitive substrate for capital projects.
        </div>
      </section>

      {/* CAPABILITIES — mission control */}
      <section id="capabilities" className="relative z-10 max-w-[1400px] mx-auto px-8 py-24 border-t border-zinc-800/70">
        <div className="text-eyebrow mb-4">THE COMMAND CANVAS</div>
        <h2 className="font-display text-3xl lg:text-[3rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-12 max-w-4xl">
          <em className="text-ember-500 font-normal">Mission control</em> for capital projects.
        </h2>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
          {CAPABILITIES.map((c) => (
            <div key={c.n} className="flex gap-5">
              <div className="font-mono text-2xl text-ember-500 tabular-nums">{c.n}</div>
              <div>
                <h3 className="text-base font-medium text-zinc-50 mb-2 leading-snug">{c.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{c.d}</p>
              </div>
            </div>
          ))}
        </div>
        <blockquote className="mt-20 border-l-2 border-ember-500 pl-8 max-w-3xl">
          <p className="font-display text-2xl lg:text-3xl font-normal text-zinc-50 leading-tight">
            Legacy tools were built to record.{' '}
            <em className="text-ember-500 font-normal">Panorix was built to decide.</em>
          </p>
        </blockquote>
      </section>

      {/* OUTCOMES */}
      <section id="outcomes" className="relative z-10 max-w-[1400px] mx-auto px-8 py-24 border-t border-zinc-800/70">
        <div className="text-eyebrow mb-4">MEASURED IMPACT</div>
        <h2 className="font-display text-3xl lg:text-[3rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-12 max-w-4xl">
          The business case, <em className="text-ember-500 font-normal">validated on real projects.</em>
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {OUTCOMES.map((o, i) => (
            <div key={i} className="border border-zinc-800/70 rounded-xl p-8 bg-gradient-to-br from-zinc-900/60 to-zinc-900/20 backdrop-blur-sm">
              <div className="font-mono text-5xl text-zinc-50 mb-4 tabular-nums tracking-tight">{o.v}</div>
              <div className="text-sm font-medium text-zinc-200 mb-3 leading-snug">{o.h}</div>
              <div className="text-xs text-zinc-500 leading-relaxed">{o.b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="relative z-10 max-w-[1400px] mx-auto px-8 py-24 border-t border-zinc-800/70">
        <div className="text-eyebrow mb-4">INDUSTRIES SERVED</div>
        <h2 className="font-display text-3xl lg:text-[3rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-12 max-w-4xl">
          Deployed across <em className="text-ember-500 font-normal">every capital-project industry</em> we serve.
        </h2>
        <div className="flex flex-wrap gap-3">
          {INDUSTRIES.map((ind, i) => (
            <span key={i} className="inline-flex items-center px-5 py-3 rounded-full border border-zinc-800/70 bg-zinc-900/40 text-sm text-zinc-300 hover:border-steel-500/60 hover:text-zinc-100 transition cursor-default">
              {ind}
            </span>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-8 py-24 border-t border-zinc-800/70">
        <div className="rounded-2xl p-12 lg:p-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(18,49,89,0.6), rgba(10,10,10,0.4))', border: '1px solid rgba(30,78,140,0.4)' }}>
          <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: 'radial-gradient(circle at 80% 20%, rgba(242,107,31,0.18), transparent 60%)' }} />
          <div className="relative">
            <div className="text-eyebrow-ember mb-4">SEE PANORIX IN MISSION MODE</div>
            <h2 className="font-display text-3xl lg:text-[3rem] font-normal leading-[1.1] tracking-[-0.015em] text-zinc-50 mb-6 max-w-4xl">
              Bring us your hardest project — <em className="text-ember-500 font-normal">we'll show you mission control for it, live.</em>
            </h2>
            <p className="text-zinc-400 max-w-2xl mb-10 leading-relaxed">
              Our "Mission Control Demo" is a 60-minute live session where we load your actual project data (under NDA),
              configure Panorix's modules to your scope, and walk your team through the integrated command canvas.
              No sanitized demos. Your data. Live.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="mailto:hello@capexara.com?subject=Mission%20Control%20Demo%20Request"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-200 px-7 py-4 text-[12px] tracking-[0.18em] uppercase rounded-md transition" data-testid="cta-request-access">
                Request Access
              </a>
              <span className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase text-zinc-500 px-2">
                Invite-only platform · Capexara managed
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-zinc-800/70 mt-12">
        <div className="max-w-[1400px] mx-auto px-8 py-10 flex flex-wrap items-center justify-between gap-4 text-[10px] tracking-[0.18em] uppercase text-zinc-500">
          <div className="flex items-center gap-3">
            <PanorixLogoCompact size={140} forceTheme="dark" />
            <span className="ml-2">— A Capexara Platform · © {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>

      {/* Auth dialog — Sign In only. Account creation is invitation-based. */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md bg-[#121212] border-zinc-800 text-zinc-100" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-100">
              <PanorixLogoCompact size={120}/> <span className="text-sm font-normal text-zinc-500">— Sign in to Control Tower</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Capexara-managed platform. Sign in with the credentials you've been issued.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-3 mt-2">
            <div>
              <Label className="text-eyebrow">Email</Label>
              <Input type="email" required value={loginEmail} onChange={e => setLoginEmail(e.target.value)} data-testid="login-email" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
            </div>
            <div>
              <Label className="text-eyebrow">Password</Label>
              <Input type="password" required value={loginPassword} onChange={e => setLoginPassword(e.target.value)} data-testid="login-password" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
            </div>
            <Button type="submit" disabled={submitting} data-testid="login-submit" variant="default" size="lg"
              className="w-full tracking-[0.18em] uppercase text-[12px]">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin"/> Signing in…</> : 'Sign In'}
            </Button>
          </form>
          <div className="mt-4 pt-4 border-t border-zinc-800 text-[11px] text-zinc-500 leading-relaxed">
            <p>This is an invite-only platform. New accounts are created when an administrator sends you an invitation email.</p>
            <p className="mt-2">If you have an invitation link, open it directly to set your password.</p>
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-800">
            <p className="text-eyebrow mb-2 text-center">Just exploring?</p>
            <Button type="button" variant="ember" onClick={() => { setShowAuth(false); setShowTrial(true); }} data-testid="dialog-trial-btn"
              className="w-full tracking-[0.18em] uppercase text-[12px]">
              <Sparkles className="w-3.5 h-3.5"/>
              Start 1-day Trial · No credit card
            </Button>
            <p className="text-[10px] text-zinc-500 mt-2 text-center">Read-only sandbox · 24-hour access</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trial registration dialog — email/password, no Google */}
      <Dialog open={showTrial} onOpenChange={setShowTrial}>
        <DialogContent className="sm:max-w-md bg-[#121212] border-zinc-800 text-zinc-100" data-testid="trial-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-100">
              <Sparkles className="w-4 h-4 text-ember-500"/> Start your 1-day Panorix trial
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              No credit card. 24 hours of read-only access to a sandbox project.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTrialRegister} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-eyebrow">First name *</Label>
                <Input required value={trialFirst} onChange={e => setTrialFirst(e.target.value)} data-testid="trial-first" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
              </div>
              <div>
                <Label className="text-eyebrow">Last name</Label>
                <Input value={trialLast} onChange={e => setTrialLast(e.target.value)} data-testid="trial-last" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
              </div>
            </div>
            <div>
              <Label className="text-eyebrow">Work email *</Label>
              <Input type="email" required value={trialEmail} onChange={e => setTrialEmail(e.target.value)} data-testid="trial-email" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
            </div>
            <div>
              <Label className="text-eyebrow">Company</Label>
              <Input value={trialCompany} onChange={e => setTrialCompany(e.target.value)} data-testid="trial-company" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
            </div>
            <div>
              <Label className="text-eyebrow">Password *</Label>
              <Input type="password" required minLength={8} value={trialPassword} onChange={e => setTrialPassword(e.target.value)} data-testid="trial-password" className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"/>
              <p className="text-[10px] text-zinc-500 mt-1">At least 8 characters.</p>
            </div>
            <Button type="submit" variant="ember" disabled={trialSubmitting} data-testid="trial-submit"
              size="lg" className="w-full tracking-[0.18em] uppercase text-[12px] mt-1">
              {trialSubmitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Creating trial…</> : <>Start 24-hour trial <ArrowRight className="w-3.5 h-3.5"/></>}
            </Button>
          </form>
          <div className="mt-3 pt-3 border-t border-zinc-800 text-[10px] text-zinc-500 leading-relaxed">
            <p>By starting a trial you agree the account expires automatically after 24 hours and your details are shared with the Capexara team.</p>
            <p className="mt-1">Already have an account? <button onClick={() => { setShowTrial(false); setShowAuth(true); }} className="text-steel-300 hover:text-zinc-100">Sign in</button></p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
